<script setup lang="ts">
/**
 * AnalysisHomePage — 2차 AI 입시분석 워크스페이스 홈(WS-00 랜딩). `/` 공개 화면.
 *
 * 034 퍼블리싱 확정본 기반 조립 전용 페이지(075 리프레시): 히어로(비로그인 full / 로그인 compact) →
 * 축 섹션(#axes — WORKSPACE_NAV 파생 노출 축을 입력→분석→적재 깔때기로 배치) 순서로
 * 섹션을 얹고, 로그인 유도 모달(LoginGateModal) 1개를 마운트한다.
 * 상단 메뉴·푸터(워크스페이스 셸)는 불변.
 *
 * 이동·게이트 분기 로직은 이 페이지가 **단일 소유**(SSoT — 034 research R6, contracts/ui-routes.md):
 *  - 이동 대상(LandingGoTarget) + 로그인   → 해당 route 즉시 이동.
 *  - 이동 대상 + 비로그인 + 로그인 필수   → 로그인 유도 모달(대상명·redirect 보존).
 *  - 이동 대상 + 비로그인 + 공개 화면     → 즉시 이동(077 — 성적 분석·진로심리검사는 샘플 뷰). 판정 원천은
 *    **목적지 라우트 meta(`requiresAuth`)** 하나 — 랜딩에 축 목록·플래그를 두지 않는다(research R7).
 *  - soon 축                              → 무동작(카드가 emit 자체를 내지 않음 — 현재 선언은 전부 open).
 *  - [시작하기]                           → 성적 보유 여부로 입시카드/정시 분석 분기(035 FR-022).
 *  - [둘러보기]                           → #axes 스크롤(useScrollTo — 헤더 보정 포함).
 *
 * 렌더 시점 서버 조회는 없다(034 FR-012) — 로그인 여부만 Pinia auth store 를 읽는다. 예외 1곳:
 * [시작하기] 클릭 시 성적 보유 조회 1회(035).
 * full-bleed 히어로 배경 때문에 PageShell 미사용(랜딩 예외 관행 — HomePage 와 동일), 컨테이너는
 * 각 섹션 컴포넌트가 scoped `.wrap`(1440·PageShell 동일 패딩)으로 소유한다.
 * 무료 콘텐츠 배너(FreeBanner)는 비노출 확정이라 마크업 자체를 두지 않는다(FR-011·R7).
 */
import { reactive, ref } from 'vue';
import { useRouter } from 'vue-router';

import LandingAxes from '#/components/fo/analysis/landing/LandingAxes.vue';
import LandingHero from '#/components/fo/analysis/landing/LandingHero.vue';
import LoginGateModal from '#/components/fo/analysis/landing/LoginGateModal.vue';
import { fetchSuneungScore } from '#/api/analysisCard';
import { useScrollTo } from '#/composables/useScrollTo';
import { LANDING_START_ROUTE, type LandingGoTarget } from '#/data/analysis/landingAxes';
import { IPSI_CARD_ROUTE, QUANT_JEONGSI_ROUTE } from '#/data/analysis/workspaceNav';
import { useAuthStore } from '#/stores/auth';

const router = useRouter();
const auth = useAuthStore();
const { scrollToId } = useScrollTo();

/** 로그인 유도 모달 상태 — 대상 축명(볼드 표기)·로그인 후 복귀지(redirect). */
const gate = reactive({
  open: false,
  targetName: null as string | null,
  redirect: LANDING_START_ROUTE,
});

function openGate(targetName: string | null, redirect: string) {
  gate.targetName = targetName;
  gate.redirect = redirect;
  gate.open = true;
}

/**
 * 이동 공용 핸들러 — 축 카드가 이 규칙을 탄다(분기 중복 금지).
 * 로그인 필수 여부는 목적지 라우트 meta 에서 읽는다(077) — 비로그인도 열 수 있는 화면(정시·수시·검사 샘플 뷰)은
 * 모달 없이 이동하고, 성적이 전제인 축(입시카드·보관함·6장)만 로그인 유도 모달을 띄운다.
 */
function goTarget(target: LandingGoTarget) {
  const needsAuth = router.resolve(target.route).meta.requiresAuth === true;
  if (auth.isAuthenticated || !needsAuth) {
    void router.push(target.route);
    return;
  }
  openGate(target.name, target.route);
}

/** [시작하기] 처리 중 — 성적 보유 조회가 끝날 때까지 재클릭(이중 조회·이중 push)을 막는다. */
const starting = ref(false);

/**
 * [시작하기]/[성적 분석 이어서 하기] — 성적 보유 여부로 도착지를 가른다(035 FR-022).
 *
 * 수능 성적을 이미 저장한 회원은 입력 화면을 다시 거칠 이유가 없으므로 정시 분석으로 바로 보내고,
 * 저장 전 회원은 입시카드(입력 허브)로 보낸다. 조회가 실패하면 입력 화면이 안전한 기본값이다
 * (분석 화면은 성적이 없으면 빈 화면이 된다).
 *
 * 비로그인은 판정 자체가 불가능하므로 로그인 유도 후 허브로 복귀시킨다.
 */
async function onStart() {
  if (!auth.isAuthenticated) {
    openGate(null, LANDING_START_ROUTE);
    return;
  }
  if (starting.value) return;
  starting.value = true;
  try {
    const score = await fetchSuneungScore();
    void router.push(score.koreanPct == null ? IPSI_CARD_ROUTE : QUANT_JEONGSI_ROUTE);
  } catch {
    // 조회 실패 — 인터셉터가 토스트를 띄웠고, 입력 화면으로 보내면 사용자가 이어서 진행할 수 있다.
    void router.push(IPSI_CARD_ROUTE);
  } finally {
    starting.value = false;
  }
}

/** [둘러보기]/[기능 둘러보기] — 페이지 내 축 카드 섹션 스크롤(화면 이동 없음, FR-009). */
function onBrowse() {
  scrollToId('axes');
}
</script>

<template>
  <LandingHero
    :authed="auth.isAuthenticated"
    :pending="starting"
    @start="onStart"
    @browse="onBrowse"
  />
  <LandingAxes @go="goTarget" />
  <LoginGateModal
    v-model:open="gate.open"
    :target-name="gate.targetName"
    :redirect="gate.redirect"
  />
</template>
